import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { platformApi } from "@/app/platform";
import {
  IconESim,
  IconFlightService,
  IconHotelService,
  IconTripService,
} from "@/components/Icons";
import {
  CommonTrackingEvents,
  FlightSearchTrackingEvents,
  HomeTrackingEvents,
  HotelSearchTrackingEvents,
  rudderAnalytics,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import type { SearchScope } from "@/types/types";

import { SearchNavigationButton } from "./SearchNavigationButton";

type NavOption = SearchScope | "eSim";

export const SearchNavigationButtons = () => {
  const intl = useIntl();

  const navigate = useNavigate();

  const handleClick = (option: NavOption) => {
    if (option === "flights") {
      rudderAnalytics.track(HomeTrackingEvents.USER_CLICKED_HOME_SERVICE, {
        service: "flight",
      });
      rudderAnalytics.track(
        FlightSearchTrackingEvents.USER_SWITCHED_TO_FLIGHT_SEARCH,
      );

      navigate({
        to: "/flights/search",
      });
    } else if (option === "hotels") {
      rudderAnalytics.track(HomeTrackingEvents.USER_CLICKED_HOME_SERVICE, {
        service: "hotel",
      });
      rudderAnalytics.track(
        HotelSearchTrackingEvents.USER_SWITCHED_TO_HOTEL_SEARCH,
      );

      navigate({
        to: "/hotels/search",
      });
    } else if (option === "trips") {
      rudderAnalytics.track(HomeTrackingEvents.USER_CLICKED_HOME_SERVICE, {
        service: "tour",
      });
      rudderAnalytics.track(TripTrackingEvents.USER_SWITCHED_TO_TRIPS);

      navigate({
        to: "/trips/search",
      });
    } else if (option === "eSim") {
      rudderAnalytics.track(HomeTrackingEvents.USER_CLICKED_HOME_SERVICE, {
        service: "eSim",
      });
      rudderAnalytics.track(CommonTrackingEvents.USER_SWITCHED_TO_ESIM);

      platformApi.navigateToMiniApp("3488020210984303");
    }
  };

  const buttons = [
    <SearchNavigationButton
      key="flights"
      onClick={() => handleClick("flights")}
    >
      <IconFlightService className="size-9 shrink-0" />
      {intl.formatMessage({
        defaultMessage: "Flights",
        description: "Navigation label for the Flights section",
      })}
    </SearchNavigationButton>,

    useFeatureIsOn("hotels-flow") ? (
      <SearchNavigationButton
        key="hotels"
        onClick={() => handleClick("hotels")}
      >
        <IconHotelService className="size-9 shrink-0" />
        {intl.formatMessage({
          defaultMessage: "Hotels",
          description: "Navigation label for the Hotels section",
        })}
      </SearchNavigationButton>
    ) : null,

    useFeatureIsOn("trips-flow") ? (
      <SearchNavigationButton key="trips" onClick={() => handleClick("trips")}>
        <IconTripService className="size-9 shrink-0" />
        {intl.formatMessage({
          defaultMessage: "Trips",
          description: "Navigation label for the Trips section",
        })}
      </SearchNavigationButton>
    ) : null,

    useFeatureIsOn("e-sim-navigation-link") ? (
      <SearchNavigationButton key="eSim" onClick={() => handleClick("eSim")}>
        <IconESim className="size-9 shrink-0" />
        {intl.formatMessage({
          defaultMessage: "e-SIM",
          description: "Navigation label for the eSIM section",
        })}
      </SearchNavigationButton>
    ) : null,
  ].filter((x): x is NonNullable<typeof x> => Boolean(x));

  return (
    <div
      className={clsx(
        "flex max-w-full px-main *:whitespace-nowrap",
        buttons.length > 3 ? "gap-3" : "gap-6",
      )}
    >
      {buttons}
    </div>
  );
};
